import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import Api from '../utils/Api';
import { useError } from './ErrorContext';
import { useLocation } from 'react-router-dom';

const PermissionsContext = createContext();

export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }
  return context;
};

export const PermissionsProvider = ({ children }) => {
  const { getToken, logout, user } = useAuth();
  const { handleApiError } = useError();
  const location = useLocation();

  const [permissions, setPermissions] = useState([]); // ✅ FIXED
  const [additionPermissions, setAdditionPermissions] = useState([]);
  const [workerAppPermissions, setWorkerAppPermissions] = useState([]);
  const [workerAppEstimationPermissions, setWorkerAppEstimationPermissions] = useState([]);
  const permissionRef = useRef(null);

  // Fetch permissions once on load
  const fetchPermissions = useCallback(async () => {
    if (!user?.id) return;
   
    const api = Api(() => getToken());
    if (!api) return;
  
    try {
      const response = await api.call(`get-permission?user_id=${user.id}`, 'GET', null, true);
  
      if (response.status === 200) {
        const newPermissions = response.data?.permissions || [];
        const newAdditionalPermissions = response.data?.additional_permissions || [];
        const newWorkerAppPermissions = response.data?.worker_app_permissions || [];
        const newWorkerAppEstimationPermissions = response.data?.worker_app_estimation_permissions || [];
         
        setPermissions({'module':newPermissions});
        setAdditionPermissions({'additional_permissions':newAdditionalPermissions});
        setWorkerAppPermissions({'worker_app_permissions':newWorkerAppPermissions});
        setWorkerAppEstimationPermissions({'worker_app_estimation_permissions':newWorkerAppEstimationPermissions});
        const updatedUser = { ...user, permissions: newPermissions,'additional_permissions':newAdditionalPermissions,'worker_app_permissions':newWorkerAppPermissions,'worker_app_estimation_permissions':newWorkerAppEstimationPermissions };
        
        localStorage.setItem('auth', JSON.stringify(updatedUser));
      } else {
        handleApiError(response.error, logout);
      }
    } catch (err) {
      handleApiError(err, logout);
    } finally {
       
    }
  }, [getToken, logout, handleApiError, user]);

  useEffect(() => {
       if (user && permissionRef.current !== location.pathname)
        { 
          fetchPermissions();
           permissionRef.current = location.pathname;
        } 
    }, [fetchPermissions, user,location]);

  // ✅ Helper function to check permission
  const permission = useCallback((module_id, field) => {
    if (module_id === true) return true;
  
    const auth = JSON.parse(localStorage.getItem('auth') || '{}');
 
    let safePermissions = auth.permissions;
  
    // ✅ FIX: normalize
    if (typeof safePermissions === 'string') {
      try {
        safePermissions = JSON.parse(safePermissions);
      } catch {
        safePermissions = [];
      }
    }
    
    if (!Array.isArray(safePermissions)) {
      safePermissions = safePermissions?.module || [];
    }
  
    const record = safePermissions.find(
      p => Number(p.module_id) === Number(module_id)
    );
   
    return record ? !!record[field] : false;
  
  }, []);

  const additionalPermission = useCallback((module_id, field) => {
    if (module_id === true) return true;
    const auth = JSON.parse(localStorage.getItem('auth') || '{}');
    let safePermissions = auth.additional_permissions;
    
    // ✅ FIX: normalize
    if (typeof safePermissions === 'string') {
      try {
        safePermissions = JSON.parse(safePermissions);
      } catch {
        safePermissions = [];
      }
    }
    
    if (!Array.isArray(safePermissions)) {
      safePermissions = safePermissions?.additional_permissions || [];
    }
  
    const record = safePermissions.find(
      p => Number(p.module_id) === Number(module_id)
    );
   
    return record ? !!record[field] : false;
  }, []);

  const workerAppPermission = useCallback((module_id, field) => {
    if (module_id === true) return true;
    const auth = JSON.parse(localStorage.getItem('auth') || '{}');
    let safePermissions = auth.worker_app_permissions;
    
    // ✅ FIX: normalize
    if (typeof safePermissions === 'string') {
      try {
        safePermissions = JSON.parse(safePermissions);
      } catch {
        safePermissions = [];
      }
    }
    
    if (!Array.isArray(safePermissions)) {
      safePermissions = safePermissions?.worker_app_permissions || [];
       
    }
  
    const record = safePermissions.find(
      p => Number(p.module_id) === Number(module_id)
    );
   
    return record ? !!record[field] : false;
  }, []);

  const workerAppEstimationPermission = useCallback((module_id, field) => {
    if (module_id === true) return true;
    const auth = JSON.parse(localStorage.getItem('auth') || '{}');
    let safePermissions = auth.worker_app_estimation_permissions;
    
    // ✅ FIX: normalize
    if (typeof safePermissions === 'string') {
      try {
        safePermissions = JSON.parse(safePermissions);
      } catch {
        safePermissions = [];
      }
    }
    
    if (!Array.isArray(safePermissions)) {
      safePermissions = safePermissions?.worker_app_estimation_permissions || [];
       
    }
  
    const record = safePermissions.find(
      p => Number(p.module_id) === Number(module_id)
    );
   
    return record ? !!record[field] : false;
  }, []);
      
  const value = {
    permissions,
    permission,
    additionalPermission,
    additionPermissions,
    workerAppPermission,
    workerAppPermissions,
    workerAppEstimationPermission,
    workerAppEstimationPermissions,
    reloadPermissions: fetchPermissions,
  };

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
};